import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Mail } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { useAuth } from "@/context/AuthContext";
import { friendlyAuthError } from "@/lib/auth";

export default function Auth() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "signin" ? "signin" : "create";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const { user, sendMagicLink, startOAuth, googleEnabled } = useAuth();

  const copy = useMemo(() => mode === "signin" ? {
    eyebrow: "Welcome back",
    title: "Return to your private space.",
    body: "Sign in to reconnect your UnAnonymous account. Your private reflection content can remain device-first.",
    submit: "Send secure sign-in link",
  } : {
    eyebrow: "Make this yours",
    title: "Create your private space.",
    body: "Your account identifies you to UnAnonymous. It does not automatically upload your journal, Mosaic, letters or private reflections.",
    submit: "Create with secure email link",
  }, [mode]);

  if (user) {
    return <Shell><div className="text-center py-10"><span className="mx-auto w-14 h-14 rounded-full grid place-items-center" style={{ background: "rgba(74,107,93,.10)", color: "var(--un-safe)" }}><Check size={22}/></span><h1 className="mt-4 text-[31px]" style={{ fontFamily: "Lora, serif", color: "var(--un-plum-deep)" }}>You’re signed in.</h1><p className="mt-2 text-[13px]" style={{ color: "var(--un-text-3)" }}>{user.email}</p><button onClick={() => nav("/home")} className="mt-6 w-full py-3.5 rounded-2xl text-[14px] font-medium" style={{ background: "var(--un-plum)", color: "#fffaf5" }}>Open UnAnonymous</button></div></Shell>;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes("@")) { setError("Enter a valid email address."); return; }
    setBusy(true);
    try {
      await sendMagicLink({ email: clean, createUser: mode === "create" });
      setSent(true);
    } catch (err) {
      setError(friendlyAuthError(err, { mode }));
    } finally { setBusy(false); }
  };

  return (
    <Shell>
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-[12px]" style={{ color: "var(--un-text-3)" }}><ArrowLeft size={15}/> Back</button>
      <div className="mt-8 text-center">
        <p className="text-[10px] uppercase tracking-[.3em]" style={{ color: "var(--un-text-3)" }}>{copy.eyebrow}</p>
        <h1 className="mt-2 text-[36px] leading-[1.04] tracking-[-.03em]" style={{ fontFamily: "Lora, serif", color: "var(--un-plum-deep)" }}>{copy.title}</h1>
        <p className="mt-4 text-[13px] leading-relaxed" style={{ color: "var(--un-text-2)" }}>{copy.body}</p>
      </div>

      {sent ? (
        <div className="mt-7 rounded-[1.5rem] p-5 text-center" style={{ background: "rgba(74,107,93,.07)", border: "1px solid rgba(74,107,93,.10)" }}>
          <Mail size={22} className="mx-auto" style={{ color: "var(--un-safe)" }}/>
          <p className="mt-3 text-[18px]" style={{ fontFamily: "Lora, serif" }}>Check your email.</p>
          <p className="mt-1 text-[12px] leading-relaxed" style={{ color: "var(--un-text-3)" }}>We sent a one-time secure sign-in link to <strong>{email}</strong>. It creates a session only after you open the link.</p>
          <button onClick={() => setSent(false)} className="mt-4 text-[12px] underline underline-offset-4" style={{ color: "var(--un-plum)" }}>Use a different email</button>
        </div>
      ) : (
        <>
          {googleEnabled && (
            <button onClick={() => startOAuth("google")} className="mt-7 w-full py-3.5 rounded-2xl text-[14px] font-medium relative un-press" style={{ border: "1px solid rgba(91,57,84,.12)", background: "rgba(255,252,248,.8)" }}><span className="absolute left-4 font-bold">G</span>Continue with Google</button>
          )}
          <div className="my-5 flex items-center gap-3"><span className="h-px flex-1" style={{ background: "rgba(91,57,84,.10)" }}/><span className="text-[10px]" style={{ color: "var(--un-text-3)" }}>secure email link</span><span className="h-px flex-1" style={{ background: "rgba(91,57,84,.10)" }}/></div>
          <form onSubmit={submit}>
            <label htmlFor="auth-email" className="block text-[11px] mb-1.5" style={{ color: "var(--un-text-3)" }}>Email address</label>
            <input id="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="you@example.com" className="w-full px-4 py-3.5 rounded-2xl outline-none" style={{ background: "#fffdf9", border: "1px solid rgba(91,57,84,.13)" }}/>
            {error && <p className="mt-2 text-[12px]" style={{ color: "#8c4f55" }}>{error}</p>}
            <button disabled={busy} className="mt-3 w-full py-3.5 rounded-2xl text-[14px] font-medium un-press disabled:opacity-50" style={{ background: "var(--un-plum)", color: "#fffaf5" }}>{busy ? "Sending…" : copy.submit}</button>
          </form>
        </>
      )}

      <button onClick={() => setMode(mode === "signin" ? "create" : "signin")} className="mt-5 w-full text-[12px]" style={{ color: "var(--un-text-3)" }}>{mode === "signin" ? "New here? Create an account" : "Already have an account? Sign in"}</button>
      <button onClick={() => nav("/home")} className="mt-2 w-full text-[12px] underline underline-offset-4" style={{ color: "var(--un-plum)" }}>Continue privately without an account</button>

      <p className="mt-6 text-[10px] leading-relaxed text-center" style={{ color: "var(--un-text-3)" }}>By continuing, you agree to the <Link className="underline" to="/terms">Terms</Link> and acknowledge the <Link className="underline" to="/privacy">Privacy Policy</Link>.</p>
    </Shell>
  );
}

function Shell({ children }) {
  return <div className="min-h-screen px-5 py-7"><div className="max-w-[450px] mx-auto"><Link to="/" className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-[.32em]" style={{ color: "var(--un-text-3)" }}><LogoMark size={30} title=""/> UnAnonymous</Link><main className="mt-4 rounded-[2rem] p-5 sm:p-7" style={{ background: "rgba(255,252,248,.82)", border: "1px solid rgba(91,57,84,.07)", boxShadow: "0 20px 60px rgba(74,45,72,.07)" }}>{children}</main></div></div>;
}
